import { ChatSource } from '../services/chatbot-ia.service';
import { isExternalHttpUrl } from './chatbot-web-favicon';

export type BuildCitePillFn = (url: string, label: string) => string;
export type HostLabelFn = (url: string | null | undefined) => string;

interface SourceCiteEntry {
  url: string;
  pillHtml: string;
  host: string;
  keywords: string[];
}

const SEARCH_ENGINE_ROOTS = [
  'duckduckgo.com',
  'google.com',
  'google.com.br',
  'bing.com',
  'search.yahoo.com',
  'search.brave.com',
];

/** Só injeta citação quando o match texto↔fonte é forte o bastante. */
const MIN_CITE_SCORE = 12;

/** Frases curtas (títulos, itens) não recebem citação automática. */
const MIN_SENTENCE_CHARS = 48;

const HOST_STOP_PARTS = new Set([
  'com',
  'br',
  'org',
  'net',
  'edu',
  'gov',
  'www',
  'html',
  'www2',
]);

const GENERIC_KEYWORDS = new Set([
  'health',
  'saude',
  'news',
  'noticias',
  'blog',
  'article',
  'artigo',
  'portal',
  'site',
  'home',
  'index',
]);

function foldMatchText(value: string): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function stripHtmlTags(value: string): string {
  return String(value ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isSearchEngineHost(host: string): boolean {
  const normalized = String(host ?? '')
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/^html\./, '');
  return SEARCH_ENGINE_ROOTS.some(
    (root) => normalized === root || normalized.endsWith(`.${root}`)
  );
}

function extractSourceKeywords(
  url: string,
  title: string,
  host: string
): string[] {
  const keywords = new Set<string>();
  const normalizedHost = host.replace(/^html\./i, '');

  for (const part of normalizedHost.split('.')) {
    const token = part.trim().toLowerCase();
    if (
      token.length >= 6 &&
      !HOST_STOP_PARTS.has(token) &&
      !GENERIC_KEYWORDS.has(token)
    ) {
      keywords.add(token);
    }
  }

  try {
    const parsed = new URL(url);
    for (const segment of parsed.pathname.split(/[/._-]+/)) {
      const token = segment.trim().toLowerCase();
      if (
        token.length >= 6 &&
        !HOST_STOP_PARTS.has(token) &&
        !GENERIC_KEYWORDS.has(token)
      ) {
        keywords.add(token);
      }
    }
  } catch {
    /* ignore */
  }

  for (const token of foldMatchText(title).split(/\s+/)) {
    if (token.length >= 6 && !GENERIC_KEYWORDS.has(token)) {
      keywords.add(token);
    }
  }

  return [...keywords];
}

function buildSourceCiteEntries(
  sources: ChatSource[],
  buildPill: BuildCitePillFn,
  hostLabel: HostLabelFn
): SourceCiteEntry[] {
  const entries: SourceCiteEntry[] = [];
  const seenUrls = new Set<string>();

  for (const source of sources) {
    const url = source.url?.trim();
    if (!url || !isExternalHttpUrl(url) || seenUrls.has(url)) continue;

    const host = hostLabel(url) || source.title || 'Site';
    if (isSearchEngineHost(host)) continue;

    seenUrls.add(url);
    entries.push({
      url,
      host,
      pillHtml: buildPill(url, host),
      keywords: extractSourceKeywords(url, source.title, host),
    });
  }

  return entries;
}

function isNumberedListMarkerPeriod(fragment: string): boolean {
  const plain = stripHtmlTags(fragment).trim();
  return /^\d+$/.test(plain);
}

function splitHtmlIntoSentences(fragment: string): string[] {
  const sentences: string[] = [];
  let current = '';
  let inTag = false;

  for (let index = 0; index < fragment.length; index += 1) {
    const char = fragment[index];
    if (char === '<') inTag = true;
    current += char;
    if (char === '>') inTag = false;

    if (!inTag && /[.!?]/.test(char)) {
      if (char === '.' && isNumberedListMarkerPeriod(current)) {
        continue;
      }

      const rest = fragment.slice(index + 1);
      if (!rest.length || /^\s/.test(rest) || rest.startsWith('<')) {
        sentences.push(current);
        current = '';
      }
    }
  }

  if (current.trim()) sentences.push(current);
  return sentences.length ? sentences : [fragment];
}

function sentenceHasCite(sentence: string): boolean {
  return sentence.includes('class="chatbot__cite"');
}

function scoreSourceForSentence(
  sentence: string,
  entry: SourceCiteEntry
): number {
  const hay = foldMatchText(stripHtmlTags(sentence));
  if (hay.length < MIN_SENTENCE_CHARS) return 0;

  let score = 0;
  const host = entry.host.toLowerCase().replace(/^html\./, '');
  const hostBrand = host.split('.')[0] ?? '';

  if (host.length >= 8 && hay.includes(host)) score += 18;
  if (hostBrand.length >= 8 && new RegExp(`\\b${escapeRegExp(hostBrand)}\\b`).test(hay)) {
    score += 16;
  }

  for (const keyword of entry.keywords) {
    if (keyword.length < 6) continue;
    if (new RegExp(`\\b${escapeRegExp(keyword)}\\b`).test(hay)) {
      score += 8;
    }
  }

  return score;
}

function pickSourcePillForSentence(
  sentence: string,
  entries: SourceCiteEntry[],
  usedSourceUrls: Set<string>
): string | null {
  if (sentenceHasCite(sentence)) return null;

  const plain = stripHtmlTags(sentence);
  if (plain.length < MIN_SENTENCE_CHARS) return null;

  let best: SourceCiteEntry | null = null;
  let bestScore = 0;

  for (const entry of entries) {
    if (usedSourceUrls.has(entry.url)) continue;

    const score = scoreSourceForSentence(sentence, entry);
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  }

  if (!best || bestScore < MIN_CITE_SCORE) return null;

  usedSourceUrls.add(best.url);
  return best.pillHtml;
}

function injectCitesIntoBlockInner(
  inner: string,
  entries: SourceCiteEntry[],
  usedSourceUrls: Set<string>
): string {
  if (!entries.length || !inner.trim()) return inner;

  const sentences = splitHtmlIntoSentences(inner);
  const rebuilt = sentences.map((sentence) => {
    const pill = pickSourcePillForSentence(sentence, entries, usedSourceUrls);
    if (!pill) return sentence;
    return `${sentence} ${pill}`;
  });

  return rebuilt.join('');
}

const BLOCK_TAG_RE = /<(p|li|blockquote)([^>]*)>([\s\S]*?)<\/\1>/gi;

/**
 * Injeta no máximo **uma** pílula `chatbot__cite` por fonte, só onde o texto
 * casa de forma forte com o host/palavras-chave da URL (sem round-robin).
 * Links markdown do modelo continuam valendo via `enhanceStreamHtml`.
 */
export function injectWebSourceCitesIntoHtml(
  html: string,
  sources: ChatSource[] | undefined,
  buildPill: BuildCitePillFn,
  hostLabel: HostLabelFn
): string {
  if (!html?.trim()) return html;

  const entries = buildSourceCiteEntries(sources ?? [], buildPill, hostLabel);
  if (!entries.length) return html;

  const usedSourceUrls = new Set<string>();

  return html.replace(BLOCK_TAG_RE, (match, tag, attrs, inner) => {
    if (!inner.trim() || inner.includes('chatbot__streamCaret')) {
      return match;
    }

    const enhanced = injectCitesIntoBlockInner(inner, entries, usedSourceUrls);
    return `<${tag}${attrs}>${enhanced}</${tag}>`;
  });
}
